<template>
  <section v-if="isInternational" class="bg-white min-h-screen">
    <div class="max-w-7xl mx-auto px-6 pt-8 pb-12">
      <!-- Breadcrumb -->
      <div class="text-xs text-gray-500">
        <NuxtLink to="/" class="hover:text-blue-600">Home</NuxtLink>
        <span class="mx-2">/</span>
        <NuxtLink to="/categories" class="hover:text-blue-600">Categories</NuxtLink>
        <span class="mx-2">/</span>
        <span class="text-gray-700">{{ categoria?.nome || 'Category' }}</span>
      </div>

      <div class="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl md:text-3xl font-extrabold text-gray-900">
            {{ categoria?.nome || 'Category' }}
          </h1>
          <div class="mt-1 text-sm text-gray-600">
            {{ sortedProdutos.length }} item{{ sortedProdutos.length === 1 ? '' : 's' }} available
          </div>
        </div>
        <div class="flex items-center gap-2">
          <div class="text-xs font-semibold text-gray-600">Sort</div>
          <select
            v-model="sort"
            class="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800"
            aria-label="Sort"
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      <div v-if="pending" class="text-center py-16 text-gray-500">Loading...</div>
      <div v-else-if="apiError" class="text-center py-16 text-red-600">
        <div class="font-semibold">Error loading category.</div>
        <div class="text-sm mt-1 text-gray-500">{{ apiError }}</div>
      </div>
      <div v-else-if="!categoria" class="text-center py-16 text-red-600">Category not found.</div>
      <div v-else-if="sortedProdutos.length === 0" class="text-center py-16 text-gray-500">
        No products available in this category yet.
      </div>
      <div v-else class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <ProductCard
          v-for="p in sortedProdutos"
          :key="p.id + (p.imagem || p.image || '')"
          :product="p"
        />
      </div>
    </div>
  </section>

  <section v-else-if="isLicencasDigitais" class="bg-white min-h-screen">
    <div class="max-w-7xl mx-auto px-6 pt-8 pb-12">
      <div class="text-xs text-gray-500">
        <NuxtLink to="/" class="hover:text-blue-600">Home</NuxtLink>
        <span class="mx-2">/</span>
        <span class="text-gray-700">{{ categoria?.nome || 'Category' }}</span>
      </div>

      <div class="mt-3 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl md:text-3xl font-extrabold text-gray-900">
            {{ categoria?.nome || 'Category' }}
          </h1>
          <div class="mt-1 text-sm text-gray-600">
            {{ produtosCountText }}
          </div>
        </div>

        <div class="flex items-center gap-2">
          <div class="text-xs font-semibold text-gray-600">Sort</div>
          <select
            v-model="sort"
            class="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800"
            aria-label="Sort"
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      <div v-if="pending" class="text-center py-16 text-gray-500">
        Loading...
      </div>

      <div v-else-if="!categoria" class="text-center py-16 text-red-600">
        Category not found.
      </div>

      <div v-else class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <ProductCard
          v-for="p in sortedProdutos"
          :key="p.id + (p.imagem || p.image || '')"
          :product="p"
        />
      </div>
    </div>
  </section>

  <section v-else class="bg-[#010d1a] min-h-screen">
    <!-- Header visual premium -->
    <CategoryHeaderPremium
      :category-name="String(categoria?.nome || 'Softwares').replace(/[\u00AD\u200B\u200C\u200D\u200E\u200F\u2028\u2029\u202A-\u202E\u2060\uFEFF]/g, '').normalize('NFC')"
      :product-count="produtos.length"
    />

    <!-- Conteúdo principal -->
    <div class="max-w-7xl mx-auto px-5 py-8">
      <div class="flex flex-col lg:flex-row gap-7 items-start">

        <!-- Sidebar -->
        <CategorySidebarPremium :current-slug="String(categoria?.slug || '')" />

        <!-- Área de grid -->
        <div class="flex-1 min-w-0">

          <!-- Loading state -->
          <div v-if="pending" class="flex items-center justify-center py-20 text-gray-400">
            <svg class="animate-spin w-6 h-6 mr-3 text-blue-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Loading products...
          </div>

          <!-- Categoria não encontrada -->
          <div v-else-if="!categoria" class="flex flex-col items-center justify-center py-20 text-center">
            <div class="text-4xl mb-4" aria-hidden="true">🔍</div>
            <h2 class="text-lg font-bold text-gray-800 mb-1">Category not found</h2>
            <p class="text-sm text-gray-500 mb-5">Check the link or go back to the home page.</p>
            <NuxtLink
              to="/"
              class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-2.5 px-5 rounded-xl transition"
            >
              Go to home
            </NuxtLink>
          </div>

          <!-- Descrição específica para Autodesk -->
          <div v-if="categoria?.slug?.includes('autodesk')" class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h3 class="text-lg font-bold text-gray-900 mb-3">About Autodesk licenses</h3>
            <p class="text-sm text-gray-700 leading-relaxed">
              Autodesk products use the annual subscription license model, which guarantees access to the latest software versions, updates and technical support. The license is linked to an Autodesk account and can be used on one device per user. After the subscription period, renewal is required to continue using the software.
            </p>
            <p class="text-sm text-gray-700 leading-relaxed mt-3">
              We are an independent store and are not affiliated with or official representatives of Autodesk. All mentioned trademarks belong to their respective owners.
            </p>
          </div>

          <!-- Grid vazio (categoria existe mas sem produtos) -->
          <div v-if="produtos.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
            <div class="text-4xl mb-4" aria-hidden="true">📦</div>
            <h2 class="text-lg font-bold text-gray-800 mb-1">No products found</h2>
            <p class="text-sm text-gray-500">This category has no products yet.</p>
          </div>

          <!-- Grid de produtos premium -->
          <CategoryGridPremium v-else>
            <ProductCard
              v-for="p in produtos"
              :key="p.id + (p.imagem || p.image || '')"
              :product="p"
            />
          </CategoryGridPremium>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
definePageMeta({ alias: ['/category/:slug'] })

const route = useRoute()
const slug = String(route.params.slug || '')
  .trim()
  .toLowerCase()

const baseUrl = useSiteUrl()
const { siteName } = useSiteBranding()
const { canonicalUrl, hreflangLinks } = useSeoLocale({ pageType: 'category', slug })

const config = useRuntimeConfig()
const storeSlug = computed(() => String((config.public as any)?.storeSlug || '').trim())

const host = computed(() => {
  if (process.server) {
    try {
      const url = useRequestURL()
      if (url?.host) return String(url.host).toLowerCase()
    } catch {
      // ignore
    }

    const headers = useRequestHeaders(['x-forwarded-host', 'x-original-host', 'host']) as Record<string, string | undefined>
    const raw = headers?.['x-forwarded-host'] || headers?.['x-original-host'] || headers?.host || ''
    const first = String(raw).split(',')[0]?.trim()
    return String(first || '').toLowerCase()
  }

  return String(window.location.host || '').toLowerCase()
})

const normalizedHost = computed(() => {
  const h0 = String(host.value || '').trim().toLowerCase()
  const h1 = h0.replace(/^https?:\/\//, '')
  const h2 = h1.replace(/\/.*/, '')
  const h3 = h2.replace(/:\d+$/, '')
  const h4 = h3.replace(/^www\./, '')
  return h4.replace(/\.$/, '')
})

const isCasaDoSoftware = computed(() => {
  if (normalizedHost.value.includes('casadosoftware.com.br')) return true
  return storeSlug.value === 'casadosoftware'
})

const isLicencasDigitais = computed(() => {
  if (normalizedHost.value.includes('licencasdigitais.com.br')) return true
  return storeSlug.value === 'licencasdigitais'
})

const isInternational = computed(() => {
  const h = normalizedHost.value
  return h.includes('gvgmall.co') || h.includes('globalsoftware.store') || storeSlug.value === 'international'
})

const { market } = useLocaleCurrency()

const apiUrl = computed(() => {
  if (isInternational.value) {
    return `/api/intl/category/${slug}?currency=${encodeURIComponent(market.value.currencyLower)}&locale=${encodeURIComponent(market.value.locale)}&countryCode=${encodeURIComponent(market.value.code)}`
  }
  return `/api/categorias/${slug}`
})

const { data, pending, error, refresh: refreshCategory } = await useFetch(() => apiUrl.value, {
  server: true,
  watch: [() => market.value.currencyLower, () => market.value.locale, () => market.value.code],
  default: () => ({
    ok: false,
    categoria: null,
    produtos: []
  })
})

watch(() => market.value, () => {
  if (isInternational.value) refreshCategory()
}, { deep: true })

const categoria = computed(() => (data.value as any)?.categoria || null)
const apiError = computed(() => error.value || (data.value as any)?._error || null)

const produtos = computed(() =>
  Array.isArray((data.value as any)?.produtos)
    ? (data.value as any).produtos
    : []
)

const sort = ref<'featured' | 'newest' | 'price_asc' | 'price_desc'>('featured')

const produtosCountText = computed(() => {
  const n = Array.isArray(produtos.value) ? produtos.value.length : 0
  return `${n} item${n === 1 ? '' : 's'}`
})

const sortedProdutos = computed(() => {
  const list = Array.isArray(produtos.value) ? [...produtos.value] : []

  if (sort.value === 'newest') {
    return list.sort((a: any, b: any) => {
      const da = new Date(a?.createdAt || a?.criadoEm || 0).getTime()
      const db = new Date(b?.createdAt || b?.criadoEm || 0).getTime()
      return db - da
    })
  }

  if (sort.value === 'price_asc') {
    return list.sort(
      (a: any, b: any) =>
        Number(a?.price ?? a?.preco ?? 0) - Number(b?.price ?? b?.preco ?? 0)
    )
  }

  if (sort.value === 'price_desc') {
    return list.sort(
      (a: any, b: any) =>
        Number(b?.price ?? b?.preco ?? 0) - Number(a?.price ?? a?.preco ?? 0)
    )
  }

  return list
})

const pageTitle = computed(() => {
  const slugValue = String(categoria.value?.slug || slug || '').trim().toLowerCase()

  if (isCasaDoSoftware.value) {
    if (slugValue.includes('antiv')) {
      return 'Antivírus Original para PC – Licenças Oficiais com Desconto'
    }
    if (slugValue.includes('windows')) {
      return 'Licenças Windows Originais – Windows 10 e 11 Pro | Casa do Software'
    }
    if (slugValue.includes('office')) {
      return 'Microsoft Office Original – Licenças Oficiais e Vitalícias'
    }
  }

  return categoria.value?.nome
    ? `${categoria.value.nome} | ${siteName}`
    : `Categoria | ${siteName}`
})

const pageDescription = computed(() => {
  const slugValue = String(categoria.value?.slug || slug || '').trim().toLowerCase()

  if (isCasaDoSoftware.value) {
    if (slugValue.includes('antiv')) {
      return 'Proteja seu computador com antivírus original e ativação imediata. Licenças oficiais com suporte e melhor preço do Brasil.'
    }
    if (slugValue.includes('windows')) {
      return 'Compre licenças Windows originais com ativação imediata e garantia vitalícia. Entrega rápida e suporte especializado.'
    }
    if (slugValue.includes('office')) {
      return 'Office 365, 2021 e versões oficiais com entrega imediata. Ative hoje mesmo com suporte e pagamento seguro.'
    }
  }

  return String((categoria.value as any)?.descricao || '')
})

useSeoMeta({
  title: pageTitle,
  description: pageDescription
})

useHead(() => ({
  link: [
    ...(canonicalUrl.value ? [{ rel: 'canonical', href: canonicalUrl.value }] : []),
    ...(hreflangLinks.value as any[])
  ]
}))
</script>